import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Search, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then(r => { setInvite(r.data); setValidating(false); }).catch(e => { setError(e.response?.data?.detail || 'Invalid invite'); setValidating(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/accept-invite', { token, name: form.name, password: form.password });
      localStorage.setItem('token', res.data.token);
      toast.success('Welcome to the team!');
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setLoading(false);
    }
  };

  if (validating) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <p className="text-destructive font-medium mb-4">{error}</p>
          <Button onClick={() => navigate('/login')} data-testid="go-login-btn">Go to Login</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="accept-invite-page">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Search className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-semibold">LaizyLeads</span>
          </div>
          <CardTitle className="text-xl">Join {invite?.company_name}</CardTitle>
          <CardDescription>You've been invited to join as {invite?.email}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Your name</Label>
              <Input placeholder="John Doe" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="invite-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" placeholder="Choose a password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required data-testid="invite-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit-btn">
              {loading ? 'Joining...' : <span className="flex items-center gap-2">Join team <ArrowRight className="w-4 h-4" /></span>}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
